/**
 * Google Identity Services ("Sign in with Google").
 *
 * The button hands back a signed Google ID token (the "credential"); the API
 * verifies it and returns its own access token. No Google secret lives in the
 * browser — only the public OAuth client id.
 */

const configuredClientId: string | undefined = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/** Undefined until a real client id is set; the "YOUR_..." placeholder counts as unset. */
export const GOOGLE_CLIENT_ID: string | undefined =
  configuredClientId && !configuredClientId.startsWith('YOUR_') ? configuredClientId : undefined;

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

type CredentialResponse = { credential: string };

type GsiButtonOptions = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
};

type GoogleIdentity = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: CredentialResponse) => void;
        ux_mode?: 'popup' | 'redirect';
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      renderButton: (parent: HTMLElement, options: GsiButtonOptions) => void;
      disableAutoSelect: () => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

let loading: Promise<GoogleIdentity> | null = null;

/** Loads the GIS script once and resolves with the `google` global. */
export function loadGoogleIdentity(): Promise<GoogleIdentity> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);

  loading ??= new Promise<GoogleIdentity>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.google?.accounts?.id
        ? resolve(window.google)
        : reject(new Error('Google sign-in failed to initialise.'));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Could not load Google sign-in. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });

  return loading;
}

/** Stops Google from silently re-selecting this account after an explicit sign-out. */
export function disableGoogleAutoSelect(): void {
  window.google?.accounts?.id?.disableAutoSelect();
}

export type { GsiButtonOptions };
